import TransportDocumentLinks from '../modules/transports/components/TransportDocumentLinks';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  type RouteOrderDto,
  type TransportRouteDetailDto,
} from '../dto/transport';
import { canDeleteTransportRoute, canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { renderTransportTypeTag } from '../modules/transports/transportRouteViews';
import { getTransportStatusDisplayTranslationKey } from '../modules/transports/transportRouteViews';
import { getTransportErrorMessage, isBlockingTransportError } from '../modules/transports/transportErrorMessages';
import { useFeedback } from '../utils/feedback';
import { buildTransportRouteLabel } from '../modules/orders/transportAddressUtils';
import { authService } from '../services/auth';
import { formatDate } from '../utils/dateUtils';
import { formatCargoQuantitySummary } from '../utils/cargoQuantity';
import { formatMoneyPl } from '../utils/currency';
import { formatUnit } from '../utils/units';
import TransportStatusPicker from '../components/TransportStatusPicker';
import {
  findStaleCargoQuantityWarnings,
  formatStaleCargoQuantityWarningItems,
  getEffectiveTransportCargoItems,
  type SharedTransportCargoItem,
} from '../components/sharedTransportFormUtils';
import {
  calculateTransportCostAllocation,
  commercialAllocationMethodLabelKey,
  commercialAllocationWarningKey,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';
import {
  DetailField,
  DetailSection,
  MetricTile,
  RouteJourney,
  ValueGrid,
} from './TransportRouteDetailView';
import './TransportRouteDetail.css';
import { TransportOrderRealizationCard } from '../modules/transports/components/TransportOrderRealizationCard';
import TransportDeliveryQuantityFields, {
  buildTransportDeliveryFormItems,
  type TransportDeliveryFormItem,
} from '../modules/transports/components/TransportDeliveryQuantityFields';
import { hasCompleteTransportCostAllocation } from '../modules/transports/transportCostAllocationPresentation';
import { buildOrderTransportPath, readTransportReturnContext, withTransportReturnContext } from '../modules/transports/transportReturnContext';
import {
  deleteTransportRoute,
  fetchTransportRoute,
  unlinkTransportOrderAndRefetch,
  updateTransportRouteAndRefetch,
} from '../modules/transports/api/transportRoutesApi';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { useDuplicateTransport } from '../modules/transports/useDuplicateTransport';
import { transportDatePickerProps } from '../components/transportFormUtils';

const { TextArea } = Input;
const { Text, Title } = Typography;

interface TransportStatusUpdateValues {
  status: string;
  actualDelivery?: Dayjs | null;
  deliveryItems?: TransportDeliveryFormItem[];
  notes?: string;
}

function getDetailCargoItemKey(item: SharedTransportCargoItem): string {
  return item.id || `${item.orderId}:${item.orderProductId || item.productName}`;
}

const TransportRouteDetail: React.FC = () => {
  const { t, i18n } = useTranslation(['transport', 'common', 'products']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnContext = readTransportReturnContext(searchParams);
  const { duplicate, duplicating } = useDuplicateTransport(returnContext?.orderId);
  const [route, setRoute] = useState<TransportRouteDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusForm] = Form.useForm();
  const editedStatus = Form.useWatch('status', statusForm);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canDeleteTransport = canDeleteTransportRoute(currentUser);
  const canViewTransportCosts = authService.canViewTransportCosts();

  useEffect(() => {
    if (!route) {
      return;
    }

    statusForm.setFieldsValue({
      status: route.status,
      actualDelivery: route.actualDelivery ? dayjs(route.actualDelivery) : null,
      deliveryItems: buildTransportDeliveryFormItems(route.cargoItems),
      notes: route.notes || undefined,
    });
  }, [route, statusForm]);

  const fetchRoute = useCallback(async (transportId: string) => {
    setLoading(true);

    try {
      setRoute(await fetchTransportRoute(transportId));
    } catch (error) {
      feedback.error(t('transport:errorFetchingRoute'));
      navigate('/app/transports');
    } finally {
      setLoading(false);
    }
  }, [navigate, t, feedback]);

  useEffect(() => {
    if (!id) {
      return;
    }

    void fetchRoute(id);
  }, [fetchRoute, id]);

  const handleRemoveOrder = async (orderId: string) => {
    if (!id) {
      return;
    }

    try {
      const result = await unlinkTransportOrderAndRefetch(id, orderId);

      feedback.success(t('transport:orderRemovedSuccessfully', { number: route?.routeNumber }));

      if (result.deleted) {
        navigate('/app/transports');
        return;
      }

      setRoute(result.route);
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorRemovingOrder', {
          number: route?.routeNumber,
        }),
        { sticky: isBlockingTransportError(error) },
      );
    }
  };

  // Owner request: the detail page had no whole-transport delete — only the
  // list page's row action. Same DELETE /transport-routes/:id endpoint and
  // canDeleteTransportRoute policy as the list, so the two surfaces can never
  // grant different access. WZ_TRANSPORT_LINK_EXISTS is mapped to a Polish
  // sentence naming the blocking documents via getTransportErrorMessage
  // instead of the raw English backend string.
  const handleDeleteTransport = async () => {
    if (!id) {
      return;
    }

    setDeleting(true);

    try {
      const deletedNumber = route?.routeNumber;
      await deleteTransportRoute(id);
      feedback.success(t('transport:messages.deleteSuccess', { number: deletedNumber }));
      navigate('/app/transports');
    } catch (error: any) {
      // A delete blocked by a linked WZ/PZ names those documents — the operator
      // has to go unlink them, so the sentence must survive the click away.
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:messages.deleteError', {
          number: route?.routeNumber,
        }),
        { sticky: isBlockingTransportError(error) },
      );
      setDeleteModalVisible(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdate = async (values: TransportStatusUpdateValues) => {
    if (!id) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: Record<string, unknown> = {
        status: values.status,
        notes: values.notes,
        ...(values.status === 'DELIVERED'
          ? {
              actualDelivery: values.actualDelivery?.format('YYYY-MM-DD') || null,
              deliveryItems: values.deliveryItems || [],
            }
          : { actualDelivery: null }),
      };
      const refreshedRoute = await submitWithOperationalException(
        (confirmedPayload) => updateTransportRouteAndRefetch(id, confirmedPayload),
        payload,
        modal,
        t,
      );
      if (!refreshedRoute) return;
      feedback.success(t('transport:statusUpdatedSuccessfully', {
        number: route?.routeNumber,
        status: t(getTransportStatusDisplayTranslationKey(values.status)),
      }));
      setStatusModalVisible(false);
      statusForm.resetFields();
      setRoute(refreshedRoute);
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorUpdatingStatus', {
          number: route?.routeNumber,
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setSubmitting(false);
    }
  };

  const statusTag = (status: string) => (
    <Tag>
      {t(getTransportStatusDisplayTranslationKey(status), { defaultValue: t('common:unknownStatus') })}
    </Tag>
  );

  // S36/S37 (coordinator triage, 2026-07-07): the LDM/weight/volume/percent
  // columns below rendered raw `.toFixed()` output — "60.000" (dot decimal,
  // no thousands grouping) instead of Polish "60,000". Same pl-PL
  // formatting convention as formatCurrencyAmount below and
  // formatConversionValue (utils/dimensionConversion.ts) for m²/m³ — single
  // local helper so every metric cell on this page agrees, rather than each
  // column hand-rolling its own toFixed/toLocaleString call.
  const formatMetricNumber = (value: number | null | undefined, fractionDigits: number): string =>
    Number(value || 0).toLocaleString('pl-PL', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  if (loading || !route) {
    return (
      <div className="transport-route-detail__loading">
        <Spin size="large" />
      </div>
    );
  }

  const totalAllocatedCost = route.routeOrders.reduce(
    (sum, routeOrder) => sum + Number(routeOrder.allocatedTransportCost || 0),
    0
  );
  const totalWeight = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.weight || 0), 0);
  const totalVolume = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.volume || 0), 0);
  // MED (m² exposure): the detail summary emphasized LDM/m³ but omitted m² —
  // match the form's "Przeliczniki ładunku" trio (formatMetricsSummary in
  // SharedTransportForm.tsx), which already shows m² / m³ / LDM together
  // whenever area is present. No legacy top-level route.totalAreaSqm column
  // exists (unlike totalWeight/totalVolume), so this sums the per-order
  // areaSqm the same way totalWeight/totalVolume above already do.
  const totalAreaSqm = route.routeOrders.reduce((sum, routeOrder) => sum + Number(routeOrder.areaSqm || 0), 0);
  const totalCargoQuantitySummary = formatCargoQuantitySummary(
    getEffectiveTransportCargoItems(route),
    i18n.language,
  );
  const allocationMethod = route.routeOrders[0]?.allocationMethod || 'LDM';
  const displayWeight = route.routeOrders.length > 0 ? totalWeight : route.totalWeight;
  const displayVolume = route.routeOrders.length > 0 ? totalVolume : route.totalVolume;
  const effectiveCargoItems = getEffectiveTransportCargoItems(route);
  // HOTEL (HIGH, "warn, never block"): a persisted cargo line's loaded
  // quantity can exceed the order product's CURRENT quantity when the order
  // was edited down after this transport was built. Non-blocking detection
  // only — no auto-resync, no save blocking. See findStaleCargoQuantityWarnings
  // for why no backend/DTO change was needed.
  const staleQuantityWarnings = findStaleCargoQuantityWarnings(route);
  // Legacy transports without stored cargo items get no (or an empty)
  // costAllocation from the API — recompute from the synthesized rows instead.
  const apiCostAllocation = route.costAllocation && route.costAllocation.items.length > 0
    ? route.costAllocation
    : null;
  const costAllocation = apiCostAllocation || calculateTransportCostAllocation({
    transportCost: route.transportRate ?? route.transportCost ?? 0,
    method: route.costAllocationMethod,
    items: effectiveCargoItems.map((item) => ({
      id: getDetailCargoItemKey(item),
      quantity: item.quantity,
      unit: item.unit,
      goodsValueTotal: item.goodsValueTotal,
      manualAllocatedTransportCost: item.manualAllocatedTransportCost,
      // Product identity + dimensions (mm) feed the BY_SQM / BY_CBM / BY_LDM
      // metric methods (normalizeItem). Without them the client-side fallback
      // allocation raises MISSING_DIMENSIONS for legacy transports even though
      // the cargo items carry dimensions. Display-only fallback path — the saved
      // allocation is untouched. Mirrors the form's cargo-row mapping (commit
      // 88e54105) and productToCargoItem above.
      name: item.productName,
      width: item.width,
      height: item.height,
      length: item.length,
      thickness: item.thickness,
    })),
  });
  const costAllocationItemsById = new Map(
    costAllocation.items.map((item) => [item.id, item]),
  );
  // MIXED_UNITS is a quantity-split-only advisory ("Podział według ilości może
  // być niepoprawny…"). A persisted allocation may still carry it even after the
  // operator moves the item-level split off BY_COMMERCIAL_QTY (to a metric/value
  // key that does not care about mixed commercial units), so only surface it
  // when the current item method is BY_COMMERCIAL_QTY.
  const visibleCostAllocationWarnings = costAllocation.warnings.filter(
    (warning) =>
      warning.code !== 'MIXED_UNITS' || route.costAllocationMethod === 'BY_COMMERCIAL_QTY',
  );
  const hasCompleteCostAllocation = hasCompleteTransportCostAllocation(costAllocation);
  const routeLabel = buildTransportRouteLabel(
    route.loadingAddresses,
    route.deliveryAddresses,
    route.originCity,
    route.destinationCity,
  );
  const deliveryDate = route.actualDelivery || route.estimatedDelivery;
  const licensePlates = [route.licensePlate1, route.licensePlate2].filter(Boolean).join(', ');
  // MED (money format): Polish-first amounts everywhere on this page —
  // "4 500,00 PLN" (pl-PL grouping + comma decimal + ISO currency-code
  // suffix), not "4500.00PLN" or a "zł" symbol. S36 (coordinator triage,
  // 2026-07-07, external run #3): this page used to mix formatCurrency's
  // "zł" symbol here with the headline Statistics' "PLN" code — formatMoneyPl
  // in utils/currency.ts is the single home for the ISO-code convention now
  // shared by every money render on the transport list + detail pages.
  const formatCurrencyAmount = (value: number | null | undefined): string =>
    value == null ? '—' : formatMoneyPl(value, route.currency);
  const renderCostAllocationWarning = (
    warning: TransportCostAllocationWarning,
    index: number,
  ) => {
    // Offending product names for MISSING_DIMENSIONS so the operator sees which
    // cargo lines lack dimensions. Unnamed lines are dropped; if none remain we
    // fall back to the generic message with no name suffix.
    const missingDimensionNames =
      warning.code === 'MISSING_DIMENSIONS'
        ? (warning.items || [])
            .map((item) => (item.name || '').trim())
            .filter((name) => name.length > 0)
            .join(', ')
        : '';

    let description: React.ReactNode;
    if (warning.code === 'MANUAL_SUM_MISMATCH') {
      description = t('transport:commercialAllocationWarningDifference', {
        difference: formatCurrencyAmount(costAllocation.summary.allocationDifference),
      });
    } else if (warning.code === 'MISSING_DIMENSIONS' && missingDimensionNames) {
      description = t('transport:missingDimensionsItems', { names: missingDimensionNames });
    }

    return (
      <Alert
        key={`${warning.code}-${warning.itemId || index}`}
        type="warning"
        showIcon
        message={t(`transport:${commercialAllocationWarningKey(warning.code)}`)}
        description={description}
        className="transport-detail-allocation-warning"
      />
    );
  };
  return (
    <div className="transport-route-detail">
      <header className="transport-detail-header">
        <div className="transport-detail-header__main">
          <div className="transport-detail-header__eyebrow">
            {renderTransportTypeTag(t, route.routeOrders.length)}
            {statusTag(route.status)}
          </div>
          <Title level={2} className="transport-detail-header__title" title={routeLabel.tooltip}>
            {routeLabel.label}
          </Title>
          <div className="transport-detail-header__meta">
            <span>{formatDate(route.loadingDate)} → {formatDate(deliveryDate)}</span>
            <Text strong>{route.transportCompany || '—'}</Text>
          </div>
        </div>
        <div className="transport-detail-header__actions">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(returnContext ? buildOrderTransportPath(returnContext.orderId) : '/app/transports')}>
            {t('common:back')}
          </Button>
          {canManageTransports ? (
            <>
              <Button icon={<CopyOutlined />} loading={duplicating} onClick={() => void duplicate(route.id)}>
                {t('transport:duplicate.action')}
              </Button>
              <Button icon={<EditOutlined />} onClick={() => navigate(withTransportReturnContext(`/app/transports/${id}/edit`, returnContext?.orderId))}>
                {t('common:edit')}
              </Button>
              <Button type="primary" disabled={route.settlementLocked} title={route.settlementLocked ? t('transport:orderTransports.settlementLocked') : undefined} onClick={() => setStatusModalVisible(true)}>
                {t('transport:updateStatus')}
              </Button>
            </>
          ) : null}
          {canDeleteTransport ? (
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={route.settlementLocked}
              title={route.settlementLocked ? t('transport:orderTransports.settlementLocked') : undefined}
              onClick={() => setDeleteModalVisible(true)}
            >
              {t('transport:messages.deleteButton')}
            </Button>
          ) : null}
        </div>
      </header>

      {/* HOTEL (HIGH, "warn, never block"): the order changed since this
          transport's cargo was built (e.g. quantity reduced) — non-blocking,
          no auto-resync. */}
      {staleQuantityWarnings.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t('transport:cargo.staleQuantityWarningTitle')}
          description={(
            <ul>
              {formatStaleCargoQuantityWarningItems(staleQuantityWarnings, t).map((line, index) => (
                <li key={staleQuantityWarnings[index].orderProductId}>{line}</li>
              ))}
            </ul>
          )}
          className="transport-detail-alert"
        />
      ) : null}

      <main className="transport-detail-layout">
        {route.warehouseDocuments !== undefined ? (
          <section className="transport-detail-documents">
            <h3>{t('transport:documents.title')}</h3>
            <TransportDocumentLinks documents={route.warehouseDocuments} />
          </section>
        ) : null}
        <div className="transport-detail-top-grid">
          <div className="transport-detail-primary-stack">
            <DetailSection title={t('transport:detailSections.routeSchedule')}>
              <RouteJourney
                loadingLabel={t('transport:fields.loadingAddresses')}
                deliveryLabel={t('transport:fields.deliveryAddresses')}
                loadingAddresses={route.loadingAddresses}
                deliveryAddresses={route.deliveryAddresses}
                dates={[
                  { label: t('transport:fields.loadingDate'), value: formatDate(route.loadingDate) },
                  { label: t('transport:fields.estimatedDelivery'), value: formatDate(route.estimatedDelivery) },
                  {
                    label: t('transport:fields.actualDelivery'),
                    value: route.actualDelivery ? formatDate(route.actualDelivery) : '—',
                  },
                ]}
                extra={!canViewTransportCosts ? (
                  <>
                    <DetailField label={t('transport:allocation.method')}>
                      {t(`transport:allocation.methods.${allocationMethod.toLowerCase()}`, {
                        defaultValue: allocationMethod,
                      })}
                    </DetailField>
                    {route.notes ? (
                      <DetailField label={t('transport:fields.notes')}>{route.notes}</DetailField>
                    ) : null}
                  </>
                ) : undefined}
              />
            </DetailSection>

            <DetailSection title={t('transport:detailSections.carrierVehicle')}>
              <ValueGrid columns={5}>
                <DetailField label={t('transport:fields.transportCompany')}>
                  {route.transportCompany || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.transportContact')}>
                  {route.transportContact || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.licensePlates')}>
                  {licensePlates || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.driverName')}>
                  {route.driverName || '—'}
                </DetailField>
                <DetailField label={t('transport:fields.driverPhone')}>
                  {route.driverPhone || '—'}
                </DetailField>
              </ValueGrid>
            </DetailSection>
          </div>

          <DetailSection title={t('transport:statistics')}>
            <div className="transport-detail-metrics">
              <MetricTile label={t('transport:totalOrders')} value={route.routeOrders.length} />
              {canViewTransportCosts ? (
                <MetricTile
                  label={t('transport:cost')}
                  value={formatMoneyPl(totalAllocatedCost, 'PLN')}
                  emphasis
                />
              ) : null}
              {totalAreaSqm > 0 ? (
                <MetricTile
                  label={t('transport:totalArea')}
                  value={`${formatMetricNumber(totalAreaSqm, 3)} m²`}
                />
              ) : null}
              <MetricTile
                label={t('transport:totalWeight')}
                value={`${formatMetricNumber(displayWeight, 2)} kg`}
              />
              <MetricTile
                label={t('transport:totalVolume')}
                value={`${formatMetricNumber(displayVolume, 3)} m³`}
              />
              <MetricTile label={t('transport:totalPackages')} value={totalCargoQuantitySummary} />
            </div>
          </DetailSection>
        </div>

        {canViewTransportCosts ? (
          <DetailSection title={t('transport:detailSections.costs')}>
            <ValueGrid columns={3} className="transport-detail-cost-grid">
              <MetricTile
                label={t('transport:fields.transportRate')}
                value={formatCurrencyAmount(route.transportRate)}
                emphasis
              />
              <DetailField label={t('transport:allocation.method')}>
                {t(`transport:allocation.methods.${allocationMethod.toLowerCase()}`, {
                  defaultValue: allocationMethod,
                })}
              </DetailField>
              <DetailField label={t('transport:commercialAllocationMethod')}>
                {t(`transport:${commercialAllocationMethodLabelKey(route.costAllocationMethod)}`)}
              </DetailField>
              {route.notes ? (
                <DetailField label={t('transport:fields.notes')} className="transport-detail-notes">
                  {route.notes}
                </DetailField>
              ) : null}
            </ValueGrid>
          </DetailSection>
        ) : null}

        {/* Etap 2 (transport przywozowy): the inbound leg. Rendered only when
            the transport actually carries supplier orders, so a purely
            outbound transport's page is unchanged. */}
        {route.supplierOrders.length > 0 ? (
          <DetailSection
            title={t('transport:supplierOrders.linkedTitle')}
            className="transport-detail-section--supplier-orders"
          >
            <ul className="transport-detail-supplier-orders" data-testid="transport-linked-supplier-orders">
              {route.supplierOrders.map((supplierOrder) => (
                <li key={supplierOrder.id}>
                  <Button
                    type="link"
                    onClick={() => navigate(`/app/supplier-orders/${supplierOrder.id}`)}
                  >
                    {supplierOrder.displayOrderNumber || supplierOrder.orderNumber}
                  </Button>
                  <Text type="secondary">{supplierOrder.supplierName || '—'}</Text>
                </li>
              ))}
            </ul>
          </DetailSection>
        ) : null}

        <DetailSection
            title={t('transport:linkedOrders')}
            className="transport-detail-section--linked-orders"
            extra={
              canManageTransports ? (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(withTransportReturnContext(`/app/transports/${id}/edit`, returnContext?.orderId))}
                >
                  {t('transport:addOrders')}
                </Button>
              ) : null
            }
          >
          <div className="transport-detail-card-list" data-testid="transport-linked-order-list">
            {/* Etap 2: an inbound-only transport has no customer orders at all
                — say so instead of leaving a blank card. */}
            {route.routeOrders.length === 0 ? (
              <Text type="secondary">{t('transport:supplierOrders.noCustomerOrders')}</Text>
            ) : null}
            {route.routeOrders.map((record: RouteOrderDto) => (
              <TransportOrderRealizationCard
                key={record.orderId}
                transport={route}
                orderId={record.orderId}
                showCosts={canViewTransportCosts}
                showOrderIdentity
                showRouteContext={false}
                onOpenOrder={() => navigate(`/app/orders/${record.order.id}?tab=transport`)}
                onUnlink={canManageTransports ? () => void handleRemoveOrder(record.orderId) : undefined}
              />
            ))}
          </div>
        </DetailSection>

        {canViewTransportCosts ? (
          <DetailSection
            title={t('transport:commercialAllocationTitle')}
            className="transport-detail-section--commercial-allocation"
          >
            <div className="transport-detail-card-list" data-testid="transport-cargo-cost-list">
              {effectiveCargoItems.map((item) => {
                const allocationItem = costAllocationItemsById.get(getDetailCargoItemKey(item));
                return (
                  <article className="transport-detail-cargo-cost" key={getDetailCargoItemKey(item)}>
                    <div className="transport-detail-cargo-cost__heading">
                      <div className="transport-detail-cargo-cost__product">{item.productName}</div>
                      <Text>{item.quantity} {formatUnit(item.unit)}</Text>
                    </div>
                    <div className="transport-detail-cargo-cost__grid">
                      <DetailField label={t('transport:commercialAllocationGoodsValue')}>
                        {formatCurrencyAmount(allocationItem?.goodsValueTotal)}
                      </DetailField>
                      <DetailField label={t('transport:goodsValueHint.canonicalTruckValue')}>
                        {item.supplierPurchaseValuePLN == null
                          ? '—'
                          : formatMoneyPl(item.supplierPurchaseValuePLN, 'PLN')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationAllocatedTransport')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.allocatedTransportCost) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationGoodsPerUnit')}>
                        {formatCurrencyAmount(allocationItem?.goodsCostPerUnit)}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerUnit')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.transportCostPerUnit) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerSqm')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.transportCostPerAreaSqm) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerCbm')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.transportCostPerVolumeM3) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTransportPerLdm')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.transportCostPerLdm) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTotalPerUnit')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.landedCostPerUnit) : t('transport:commercialAllocationPending')}
                      </DetailField>
                      <DetailField label={t('transport:commercialAllocationTotal')}>
                        {hasCompleteCostAllocation ? formatCurrencyAmount(allocationItem?.landedCostTotal) : t('transport:commercialAllocationPending')}
                      </DetailField>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="transport-detail-allocation-summary">
              <div className="transport-detail-allocation-summary__item">
                {t('transport:commercialAllocationSummaryGoodsValue', {
                  value: formatCurrencyAmount(costAllocation.summary.totalGoodsValue),
                })}
              </div>
              {hasCompleteCostAllocation ? (
                <>
                  <div className="transport-detail-allocation-summary__item">
                    {t('transport:commercialAllocationSummaryTransport', {
                      value: formatCurrencyAmount(costAllocation.summary.totalAllocatedTransportCost),
                    })}
                  </div>
                  <div className="transport-detail-allocation-summary__item transport-detail-allocation-summary__item--total">
                    <strong>
                      {t('transport:commercialAllocationSummaryTotal', {
                        value: formatCurrencyAmount(costAllocation.summary.totalLandedCost),
                      })}
                    </strong>
                  </div>
                </>
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  message={t('transport:commercialAllocationIncompleteTitle')}
                  description={t('transport:commercialAllocationIncompleteDescription', {
                    value: formatCurrencyAmount(route.transportRate ?? route.transportCost ?? 0),
                  })}
                />
              )}
            </div>
            {visibleCostAllocationWarnings.map(renderCostAllocationWarning)}
          </DetailSection>
        ) : null}
      </main>

      <Modal
        title={t('transport:updateRouteStatus')}
        open={statusModalVisible}
        onCancel={() => {
          statusForm.setFieldsValue({
            status: route.status,
            actualDelivery: route.actualDelivery ? dayjs(route.actualDelivery) : null,
            deliveryItems: buildTransportDeliveryFormItems(route.cargoItems),
            notes: route.notes || undefined,
          });
          setStatusModalVisible(false);
        }}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item
            name="status"
            label={t('transport:fields.status')}
            rules={[{ required: true, message: t('transport:pleaseSelectStatus') }]}
          >
            <TransportStatusPicker />
          </Form.Item>
          {editedStatus === 'DELIVERED' ? (
            <>
              <Form.Item
                name="actualDelivery"
                label={t('transport:fields.actualDelivery')}
                rules={[{ required: true, message: t('transport:validation.actualDeliveryRequired') }]}
              >
                <DatePicker
                  placeholder={t('transport:placeholders.selectDate')}
                  {...transportDatePickerProps}
                />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                message={t('transport:actualDeliveryRequiredHint')}
                style={{ marginBottom: 16 }}
              />
              <TransportDeliveryQuantityFields
                cargoItems={route.cargoItems}
                routeOrders={route.routeOrders}
              />
            </>
          ) : null}
          <Form.Item name="notes" label={t('transport:notes')}>
            <TextArea rows={3} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('transport:updateStatus')}
            </Button>
            <Button
              onClick={() => {
                setStatusModalVisible(false);
                statusForm.resetFields();
              }}
            >
              {t('common:cancel')}
            </Button>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={t('transport:messages.deleteButton')}
        open={deleteModalVisible}
        onOk={() => void handleDeleteTransport()}
        onCancel={() => setDeleteModalVisible(false)}
        okButtonProps={{ danger: true, loading: deleting }}
        okText={t('common:delete')}
        cancelText={t('common:cancel')}
      >
        <p>{t('transport:messages.deleteConfirmTitle', { number: route.routeNumber })}</p>
      </Modal>
    </div>
  );
};

export default TransportRouteDetail;
